<script lang="ts">
  import { app, type View } from "../../state/app.svelte";
  import { i18n, type Locale } from "../../i18n/i18n.svelte";

  const version = __APP_VERSION__;

  const fr = {
    views: { chat: "Chat", docs: "Docs", history: "Historique" } as Record<View, string>,
    settings: "Configuration",
    noActiveDoc: "Aucun document actif",
    noActiveDocHelp:
      "Pour interroger tes documents : importe-les dans l'onglet Docs, puis coche ceux que l'agent doit utiliser. Ils deviennent « actifs ».",
    language: "Langue",
  };
  const en: typeof fr = {
    views: { chat: "Chat", docs: "Docs", history: "History" },
    settings: "Settings",
    noActiveDoc: "No active document",
    noActiveDocHelp:
      "To query your documents: upload them in the Docs tab, then tick the ones the agent should use. They become “active”.",
    language: "Language",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  // Both languages are shown, the current one highlighted: a single button
  // naming the OTHER language read as the current state, the wrong way round.
  const LANGUAGES: Array<{ locale: Locale; name: string }> = [
    { locale: "fr", name: "Français" },
    { locale: "en", name: "English" },
  ];
</script>

<header>
  <div class="top">
    <!-- EPFL logo, inlined from elements/svg/epfl-logo.svg. Elements ships it
         with a `<style>.cls-1{fill:red}</style>` block; that is dropped here so
         the mark inherits `currentColor` and stays a single token to retheme.
         The rule and the service name after it are the standard EPFL lockup for
         a tool that is not epfl.ch itself. -->
    <svg
      class="logo"
      viewBox="0 0 182.4 53"
      role="img"
      aria-label="EPFL"
      fill="currentColor"
    >
      <polygon points="0 21.6 11.43 21.6 11.43 9.8 38.34 9.8 38.34 0 0 0 0 21.6" />
      <polygon points="0 53 38.34 53 38.34 43.2 11.43 43.2 11.43 31.4 0 31.4 0 53" />
      <rect x="11.43" y="21.6" width="24.61" height="9.8" />
      <path d="M86,4.87a16.12,16.12,0,0,0-5.68-3.53A23.76,23.76,0,0,0,71.82,0H48.14V53H59.57V31.4H71.82a23.76,23.76,0,0,0,8.46-1.34A16.12,16.12,0,0,0,86,26.53a13.43,13.43,0,0,0,3.19-5,17.38,17.38,0,0,0,0-11.62A13.52,13.52,0,0,0,86,4.87ZM78,18.73a5.7,5.7,0,0,1-2.26,1.8,11.33,11.33,0,0,1-3.27.85,32,32,0,0,1-3.86.22H59.57V9.8h9.05a32,32,0,0,1,3.86.22,11,11,0,0,1,3.27.86A5.59,5.59,0,0,1,78,12.67a5,5,0,0,1,.86,3A5,5,0,0,1,78,18.73Z" />
      <polygon points="155.47 43.2 155.47 0 144.04 0 144.04 53 182.38 53 182.38 43.2 155.47 43.2" />
      <polygon points="97.42 21.6 108.85 21.6 108.85 9.8 135.76 9.8 135.76 0 97.42 0 97.42 21.6" />
      <rect x="97.42" y="31.4" width="11.43" height="21.6" />
      <rect x="108.85" y="21.6" width="24.61" height="9.8" />
    </svg>
    <span class="rule" aria-hidden="true"></span>
    <h1>Personal RAG</h1>
    <span class="version">v{version}</span>
    <div class="lang-switch" role="group" aria-label={t.language}>
      {#each LANGUAGES as lang (lang.locale)}
        <button
          class:current={i18n.locale === lang.locale}
          aria-pressed={i18n.locale === lang.locale}
          title={lang.name}
          lang={lang.locale}
          onclick={() => i18n.set(lang.locale)}
        >{lang.locale.toUpperCase()}</button>
      {/each}
    </div>
    <button
      class="icon-btn"
      title={t.settings}
      onclick={() => (app.showConfig = !app.showConfig)}
      aria-label={t.settings}
    >⚙</button>
  </div>

  {#if app.activeDocs.length > 0}
    <div class="active-docs" title={app.activeDocs.map((d) => d.name).join(", ")}>
      📄 {app.activeDocs.map((d) => d.name).join(" · ")}
    </div>
  {:else}
    <!-- The label ellipsizes (overflow:hidden), which would clip the bubble, so
         the bubble is a sibling of it inside a non-clipping wrapper. -->
    <div class="tooltip-wrap" tabindex="0" role="note" aria-label={t.noActiveDocHelp}>
      <div class="active-docs muted">{t.noActiveDoc}</div>
      <span class="tooltip" role="tooltip">{t.noActiveDocHelp}</span>
    </div>
  {/if}

  <nav class="tabs">
    {#each Object.entries(t.views) as [v, label] (v)}
      <button
        class:active={app.view === v}
        onclick={() => (app.view = v as View)}
      >
        {label}
      </button>
    {/each}
  </nav>
</header>

<style>
  header {
    border-bottom: 1px solid var(--epfl-gray-100);
    padding: 0.75rem 1rem 0;
    background: var(--epfl-white);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .logo {
    height: 1.05rem;
    width: auto;
    flex: none;
    /* The logo is the one mark that stays EPFL red: it is the brand, not an
       accent. */
    color: var(--epfl-red);
  }

  .rule {
    width: 1px;
    height: 1.1rem;
    background: var(--epfl-gray-200);
    flex: none;
  }

  h1 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .version {
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
    /* Pushes the gear to the far right without a wrapper element. */
    margin-right: auto;
  }

  .icon-btn {
    background: none;
    border: none;
    font-size: 1.1rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: var(--epfl-radius);
    font-family: inherit;
    line-height: 1;
  }

  .icon-btn:hover:not(:disabled) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .lang-switch {
    display: flex;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    overflow: hidden;
  }

  .lang-switch button {
    background: none;
    border: none;
    padding: 0.2rem 0.4rem;
    font-family: inherit;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--epfl-gray-600);
    cursor: pointer;
    line-height: 1.2;
  }

  .lang-switch button:hover:not(.current) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .lang-switch button.current {
    background: var(--epfl-accent);
    color: var(--epfl-white);
    cursor: default;
  }

  .active-docs {
    font-size: 0.75rem;
    color: var(--epfl-gray-700);
    padding: 0.5rem 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .muted {
    color: var(--epfl-gray-400);
  }

  /* Same bubble as the toolbar tooltips in ChatView. */
  .tooltip-wrap {
    position: relative;
    display: block;
    cursor: help;
  }

  .tooltip-wrap .tooltip {
    position: absolute;
    top: calc(100% - 2px);
    left: 0;
    z-index: 50;
    background: var(--epfl-text);
    color: var(--epfl-white);
    padding: 0.45rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.72rem;
    line-height: 1.35;
    width: 260px;
    max-width: 100%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    opacity: 0;
    transform: translateY(-2px);
    pointer-events: none;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }

  .tooltip-wrap .tooltip::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 1rem;
    border: 5px solid transparent;
    border-bottom-color: var(--epfl-text);
  }

  .tooltip-wrap:hover .tooltip,
  .tooltip-wrap:focus-within .tooltip {
    opacity: 1;
    transform: translateY(0);
  }

  .tabs {
    display: flex;
    gap: 0.25rem;
    margin-top: 0.25rem;
  }

  .tabs button {
    background: none;
    border: none;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
    /* 3px so the active bar reads as an accent rule rather than a hairline;
       transparent on the others keeps the row from shifting. */
    border-bottom: 3px solid transparent;
    font-family: inherit;
  }

  .tabs button:hover {
    color: var(--epfl-text);
  }

  .tabs button.active {
    color: var(--epfl-accent);
    border-bottom-color: var(--epfl-accent);
    font-weight: 700;
  }
</style>
